import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteConfirmModal from "./delete-confirm-modal";
import {
  adminCourseStepSectionGetAction,
  adminStepSectionQuestionDeleteAction,
  adminStepSectionQuestionsListAction
} from "../Action/action";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

export default function StepSectionQuestions() {
  const { sectionId } = useParams();
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 50 });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadMeta() {
      try {
        const sRes = await adminCourseStepSectionGetAction(sectionId);
        if (cancelled) return;
        if (sRes?.success) setSection(sRes.data || null);
      } catch (e) {}
    }
    if (sectionId) loadMeta();
    return () => {
      cancelled = true;
    };
  }, [sectionId]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const res = await adminStepSectionQuestionsListAction(sectionId, { page, per_page: perPage });
        if (cancelled) return;
        if (!res?.success) throw new Error(res?.message || "Failed to load questions.");
        setRows(Array.isArray(res.data) ? res.data : []);
        setMeta({
          total: Number(res?.meta?.total || 0),
          page: Number(res?.meta?.page || page),
          per_page: Number(res?.meta?.per_page || perPage)
        });
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setError(e?.response?.data?.message || e?.message || "Failed to load questions.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (sectionId) load();
    return () => {
      cancelled = true;
    };
  }, [sectionId, page, perPage, reloadNonce]);

  const viewRows = useMemo(() => {
    return rows.map((r) => {
      const optCount = Number(r?.option_count ?? 0);
      let typeLabel = "Single answer";
      if (r?.question_type === "multiple") typeLabel = "Multiple answers";
      else if (r?.question_type === "text") typeLabel = "Text answer";
      else if (r?.question_type === "single" && optCount === 0) typeLabel = "Text answer (legacy)";
      return {
        raw: r,
        id: r?.id,
        title: r?.title || "—",
        displayOrder: r?.display_order != null && r?.display_order !== "" ? String(r.display_order) : "0",
        questionType: typeLabel,
        statusLabel: Number(r?.is_active) === 1 ? "Active" : "Inactive",
        created: formatDate(r?.created_on)
      };
    });
  }, [rows]);

  const total = meta?.total ? Number(meta.total) : viewRows.length;
  const totalPages = total ? Math.max(1, Math.ceil(total / perPage)) : 1;
  const start = total && viewRows.length ? (page - 1) * perPage + 1 : 0;
  const end = total && viewRows.length ? start + viewRows.length - 1 : 0;

  const pageButtons = useMemo(() => {
    const max = 5;
    const safeTotal = Math.max(1, totalPages);
    const current = Math.min(Math.max(1, page), safeTotal);
    let s = Math.max(1, current - 2);
    let e = Math.min(safeTotal, s + max - 1);
    s = Math.max(1, e - max + 1);
    const arr = [];
    for (let i = s; i <= e; i += 1) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const sectionsBase = "/step-sections";

  const openDelete = (row) => {
    if (!row?.id) return;
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    try {
      const res = await adminStepSectionQuestionDeleteAction(sectionId, deleteRow.id);
      if (!res?.success) throw new Error(res?.message || "Delete failed");
      toast.success("Question deleted", { toastId: "ssq-delete" });
      setDeleteOpen(false);
      setDeleteRow(null);
      setReloadNonce((n) => n + 1);
    } catch (e) {
      toast.error(e?.message || "Delete failed", { toastId: "ssq-delete" });
    } finally {
      setDeleting(false);
    }
  };

  const sectionTitle = section?.title || "Step section";
  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Questions unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="breadcrumb">
        <Link to={sectionsBase}>Step sections</Link>
        <span>›</span>
        <span>{sectionTitle}</span>
        <span>›</span>
        <span>Questions</span>
      </div>

      <div className="toolbar">
        <div className="flex items-center gap-3">
          <Link to={sectionsBase} className="btn btn-outline btn-sm">
            ← Step sections
          </Link>
          <Link to="new" className="btn btn-primary">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            Add question
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Display order</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : (
                viewRows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong>{r.title}</strong>
                    </td>
                    <td>{r.questionType}</td>
                    <td>{r.displayOrder}</td>
                    <td>
                      <span className={`badge ${r.statusLabel === "Active" ? "badge-success" : "badge-gray"}`}>
                        {r.statusLabel}
                      </span>
                    </td>
                    <td>{r.created}</td>
                    <td>
                      <div className="actions-cell">
                        <Link to={`${r.id}/edit`} className="action-link">
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="action-link text-danger"
                          onClick={() => openDelete(r.raw)}
                          style={{ background: "transparent", border: "none", padding: 0 }}
                        >
                          Delete
                        </button>
                    
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {!loading && viewRows.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state">
                      <div className="empty-icon">🫥</div>
                      No questions yet.
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <span>
            {total === 0
              ? "No questions"
              : `Showing ${start}–${end} of ${total} Question${total === 1 ? "" : "s"} · Page ${page} of ${totalPages}`}
          </span>
          {total > 0 ? (
            <div className="pagination-pages">
              <button
                type="button"
                onClick={() => setPage(1)}
                disabled={page <= 1 || loading}
                title="First page"
                aria-label="First page"
              >
                {"‹‹"}
              </button>
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
                ‹
              </button>
              {pageButtons.map((p) => (
                <button
                  key={p}
                  className={p === page ? "active" : ""}
                  type="button"
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
              >
                ›
              </button>
              <button
                type="button"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages || loading}
                title="Last page"
                aria-label="Last page"
              >
                {"››"}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <DeleteConfirmModal
        open={deleteOpen}
        itemType="question"
        itemName={deleteRow?.title || "Question"}
        onCancel={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={confirmDelete}
        confirmDisabled={deleting}
      />
    </div>
  );
}
